/**
 * ConversationList Component
 * Displays a sidebar list of user conversations with options to select, delete, or create new
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FaPlus, FaTrash, FaEdit, FaCheck, FaTimes, FaSync, FaFileAlt, FaColumns, FaThumbtack, FaSearch, FaChevronDown } from 'react-icons/fa';
import { listConversations, deleteConversation, updateConversation } from '../api/conversationApi';
import './ConversationList.css';

const PINNED_STORAGE_KEY = 'p2s_pinned_conversations';

const ConversationList = ({ 
  accessToken, 
  currentConversationId, 
  onSelectConversation, 
  onNewConversation,
  onConversationDeleted,
  refreshTrigger,
  onDocumentManagerClick,
  toggleSidebar,
  isCollapsed,
  documentManagerContent
}) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortMode, setSortMode] = useState('recent');
  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef(null);
  const [showOnlyPinned, setShowOnlyPinned] = useState(false);
  const [pinnedConversationIds, setPinnedConversationIds] = useState(() => {
    try {
      const stored = localStorage.getItem(PINNED_STORAGE_KEY);
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const loadConversations = useCallback(async () => {
    if (!accessToken) {
      return;
    }

    try {
      setLoading(true);
      const data = await listConversations(accessToken, false);
      setConversations(data.conversations || []);
    } catch (error) {
      console.error('Failed to load conversations:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations, refreshTrigger]);

  useEffect(() => {
    localStorage.setItem(PINNED_STORAGE_KEY, JSON.stringify(pinnedConversationIds));
  }, [pinnedConversationIds]);

  // Close sort dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sortRef.current && !sortRef.current.contains(e.target)) {
        setSortOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [confirmingDeleteId, setConfirmingDeleteId] = useState(null);

  const handleDelete = async (conversationId, e) => {
    e.stopPropagation();
    
    if (confirmingDeleteId === conversationId) {
      // Second click: actually delete
      try {
        await deleteConversation(accessToken, conversationId);
        setConversations(prev => prev.filter(c => c.id !== conversationId));
        setConfirmingDeleteId(null);
        
        // If deleting current conversation, notify parent
        if (conversationId === currentConversationId) {
          onConversationDeleted();
        }
      } catch (error) {
        console.error('Failed to delete conversation:', error);
        setConfirmingDeleteId(null);
      }
    } else {
      // First click: enter confirmation state
      setConfirmingDeleteId(conversationId);
      // Auto-cancel after 3 seconds
      setTimeout(() => setConfirmingDeleteId(prev => prev === conversationId ? null : prev), 3000);
    }
  };

  const handleStartEdit = (conversation, e) => {
    e.stopPropagation();
    setEditingId(conversation.id);
    setEditTitle(conversation.title);
  };

  const handleSaveEdit = async (conversationId, e) => {
    e.stopPropagation();
    
    try {
      await updateConversation(accessToken, conversationId, { title: editTitle });
      setConversations(prev => prev.map(c => 
        c.id === conversationId ? { ...c, title: editTitle } : c
      ));
      setEditingId(null);
    } catch (error) {
      console.error('Failed to update conversation:', error);
      alert('Failed to update title');
    }
  };

  const handleCancelEdit = (e) => {
    e.stopPropagation();
    setEditingId(null);
    setEditTitle('');
  };

  const togglePinnedConversation = (conversationId, e) => {
    e.stopPropagation();
    setPinnedConversationIds(prev => (
      prev.includes(conversationId)
        ? prev.filter(id => id !== conversationId)
        : [conversationId, ...prev]
    ));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const filteredConversations = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    let filtered = conversations;

    if (query) {
      filtered = filtered.filter(conversation => {
        const title = (conversation.title || '').toLowerCase();
        const mode = (conversation.mode || 'general').toLowerCase();
        return title.includes(query) || mode.includes(query);
      });
    }

    if (showOnlyPinned) {
      const pinnedSet = new Set(pinnedConversationIds);
      filtered = filtered.filter(conversation => pinnedSet.has(conversation.id));
    }

    const sorted = [...filtered].sort((a, b) => {
      const dateA = new Date(a.updated_at).getTime();
      const dateB = new Date(b.updated_at).getTime();

      if (sortMode === 'oldest') {
        return dateA - dateB;
      }

      if (sortMode === 'messages') {
        return (b.total_messages || 0) - (a.total_messages || 0);
      }

      return dateB - dateA;
    });

    return sorted;
  }, [conversations, searchTerm, showOnlyPinned, pinnedConversationIds, sortMode]);

  const groupedConversations = useMemo(() => {
    const groups = [
      { key: 'general', label: 'General' },
      { key: 'hr', label: 'HR' },
      { key: 'engineering', label: 'Engineering' },
    ];

    const pinnedSet = new Set(pinnedConversationIds);
    const pinned = filteredConversations.filter(conversation => pinnedSet.has(conversation.id));
    const unpinnedByMode = groups.map(group => ({
      ...group,
      conversations: filteredConversations.filter(
        conversation => (conversation.mode || 'general') === group.key && !pinnedSet.has(conversation.id)
      )
    }));

    return { pinned, unpinnedByMode };
  }, [filteredConversations, pinnedConversationIds]);

  const renderConversationItem = (conv) => (
    <div
      key={conv.id}
      className={`conversation-item ${conv.id === currentConversationId ? 'active' : ''} ${pinnedConversationIds.includes(conv.id) ? 'pinned' : ''}`}
      onClick={() => onSelectConversation(conv.id)}
    >
      {editingId === conv.id ? (
        <div className="conversation-edit" onClick={e => e.stopPropagation()}>
          <input
            type="text"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSaveEdit(conv.id, e);
              if (e.key === 'Escape') handleCancelEdit(e);
            }}
            autoFocus
          />
          <div className="conversation-edit-actions">
            <button onClick={(e) => handleSaveEdit(conv.id, e)} title="Save">
              <FaCheck />
            </button>
            <button onClick={handleCancelEdit} title="Cancel">
              <FaTimes />
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="conversation-item-content">
            <div className="conversation-title">{conv.title}</div>
            <div className="conversation-meta">
              <span className="conversation-time">{formatDate(conv.updated_at)}</span>
              <span className="conversation-stats">
                {conv.total_messages} msg • {
                  conv.total_tokens < 1000
                    ? `${conv.total_tokens} tokens`
                    : `${(conv.total_tokens / 1000).toFixed(1)}k tokens`
                }
              </span>
            </div>
          </div>
          <div className="conversation-actions">
            <button
              onClick={(e) => togglePinnedConversation(conv.id, e)}
              title={pinnedConversationIds.includes(conv.id) ? 'Unpin' : 'Pin'}
              className={`conversation-action-btn pin ${pinnedConversationIds.includes(conv.id) ? 'active' : ''}`}
            >
              <FaThumbtack />
            </button>
            <button
              onClick={(e) => handleStartEdit(conv, e)}
              title="Rename"
              className="conversation-action-btn"
            >
              <FaEdit />
            </button>
            <button
              onClick={(e) => handleDelete(conv.id, e)}
              title={confirmingDeleteId === conv.id ? 'Click again to confirm' : 'Delete'}
              className={`conversation-action-btn delete ${confirmingDeleteId === conv.id ? 'confirming' : ''}`}
            >
              {confirmingDeleteId === conv.id ? <FaCheck /> : <FaTrash />}
            </button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className={`conversation-list${isCollapsed ? ' collapsed' : ''}`}>
      {isCollapsed ? (
        /* Icon rail — shown when sidebar is collapsed */
        <div className="sidebar-icon-rail">
          <button className="rail-btn" onClick={toggleSidebar} title="Expand sidebar" aria-label="Expand sidebar">
            <FaColumns />
          </button>
          <button
            className={`rail-btn ${showOnlyPinned ? 'active' : ''}`}
            onClick={() => setShowOnlyPinned(prev => !prev)}
            title={showOnlyPinned ? 'Show all chats' : 'Show pinned only'}
            aria-label={showOnlyPinned ? 'Show all chats' : 'Show pinned only'}
          >
            <FaThumbtack />
          </button>
          <button className="rail-btn" onClick={onNewConversation} title="New chat" aria-label="New chat">
            <FaPlus />
          </button>
          <button className="rail-btn" onClick={loadConversations} title="Refresh" aria-label="Refresh conversations">
            <FaSync />
          </button>
          <button className="rail-btn" onClick={onDocumentManagerClick} title="My Files" aria-label="Document Manager">
            <FaFileAlt />
          </button>
        </div>
      ) : (
        <>
          <div className="conversation-list-content">
            <div className="conversation-list-header">
              <h3>Chats</h3>
              <div className="conversation-header-actions">
                <button
                  className="refresh-btn"
                  onClick={loadConversations}
                  title="Refresh conversations"
                  aria-label="Refresh conversations"
                >
                  <FaSync />
                </button>
                <button
                  className="new-conversation-btn"
                  onClick={onNewConversation}
                  title="New Conversation"
                  aria-label="Start new conversation"
                >
                  <FaPlus />
                </button>
                {toggleSidebar && (
                  <button
                    className="collapse-sidebar-btn"
                    onClick={toggleSidebar}
                    title="Collapse sidebar"
                    aria-label="Collapse sidebar"
                  >
                    <FaColumns />
                  </button>
                )}
              </div>
            </div>

            <div className="conversation-search">
              <FaSearch className="conversation-search-icon" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search conversations"
                aria-label="Search conversations"
              />
            </div>

            <div className="conversation-filter-row">
              <div className="conversation-sort-root" ref={sortRef}>
                <button
                  className="conversation-sort-trigger"
                  onClick={() => setSortOpen(o => !o)}
                  aria-haspopup="listbox"
                  aria-expanded={sortOpen}
                  aria-label="Sort conversations"
                >
                  <span className="conversation-sort-label">
                    {sortMode === 'recent' ? 'Recent' : sortMode === 'oldest' ? 'Oldest' : 'Most messages'}
                  </span>
                  <FaChevronDown className={`conversation-sort-chevron${sortOpen ? ' conversation-sort-chevron-open' : ''}`} />
                </button>
                {sortOpen && (
                  <ul className="conversation-sort-dropdown" role="listbox">
                    {[{ value: 'recent', label: 'Recent' }, { value: 'oldest', label: 'Oldest' }, { value: 'messages', label: 'Most messages' }].map(opt => (
                      <li
                        key={opt.value}
                        role="option"
                        aria-selected={sortMode === opt.value}
                        className={`conversation-sort-option${sortMode === opt.value ? ' conversation-sort-option-active' : ''}`}
                        onClick={() => { setSortMode(opt.value); setSortOpen(false); }}
                      >
                        <span>{opt.label}</span>
                        {sortMode === opt.value && <span className="conversation-sort-check">✓</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <button
                className={`conversation-pin-filter ${showOnlyPinned ? 'active' : ''}`}
                onClick={() => setShowOnlyPinned(prev => !prev)}
                title={showOnlyPinned ? 'Show all chats' : 'Show pinned only'}
              >
                <FaThumbtack /> {showOnlyPinned ? 'Pinned only' : 'All chats'}
              </button>
            </div>

            {loading ? (
              <div className="conversation-list-loading">Loading...</div>
            ) : conversations.length === 0 ? (
              <div className="conversation-list-empty">
                <p>No conversations yet.</p>
                <button
                  className="empty-state-new-chat-btn"
                  onClick={onNewConversation}
                >
                  <FaPlus /> Start a new chat
                </button>
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="conversation-list-empty">
                <p>No matching chats found.</p>
              </div>
            ) : (
              <div className="conversation-list-items">
                {groupedConversations.pinned.length > 0 && (
                  <div className="conv-group">
                    <div className="conv-group-label">Pinned</div>
                    {groupedConversations.pinned.map(renderConversationItem)}
                  </div>
                )}

                {groupedConversations.unpinnedByMode.map(({ key, label, conversations: groupItems }) => {
                  if (groupItems.length === 0) return null;
                  return (
                    <div key={key} className="conv-group">
                      <div className="conv-group-label">{label}</div>
                      {groupItems.map(renderConversationItem)}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="conversation-list-footer">
            <button
              className="document-manager-btn"
              onClick={onDocumentManagerClick}
              title="My Files"
            >
              <FaFileAlt /> My Files
            </button>
          </div>

          {/* Document Manager Container */}
          {documentManagerContent && (
            <div className="conversation-list-doc-manager">
              {documentManagerContent}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ConversationList;